/**
 * WhiteboardProjectionView — the D2P "checkout to physical" artifact, now
 * interactive (rung 3).
 *
 * A full-screen rendering of a board's current state (boxes at their captured
 * positions, arrows between them, anchor codes) meant to be PROJECTED onto a
 * physical whiteboard — or shown on a touch panel. Two ways to round-trip:
 *
 *  • Additive (any projector): project v1, draw new boxes around it with marker,
 *    re-capture — fiducial registration matches the existing items exactly.
 *  • Direct edit (touch panel): turn on Edit, DRAG a box to move it or WIPE it
 *    away to delete it, then Save — writes a new version with identities
 *    preserved and updates the live items (altitude / archive) without a photo.
 *
 * Two display modes: "Projector" (black background, bright shapes) and "Screen"
 * (light background — for a TV/touch panel). Corner registration marks help
 * align an additive re-capture.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { X, MonitorPlay, Sun, Moon, Pencil, Check, Trash2, Loader2, RotateCcw, Image, LayoutGrid } from 'lucide-react';
import type { BoardBaseline } from '../../lib/whiteboardMerge';
import type { BoardEdit } from '../../lib/whiteboardProjectionEdit';
import { boardToSurfacePct, surfaceToBoard } from '../../lib/whiteboardProjection';

type Mode = 'projector' | 'screen';
type Artifact = 'structured' | 'original';
type Pos = { x: number; y: number };

interface Props {
  baseline: BoardBaseline;
  title: string;
  onClose: () => void;
  /** JPEG data URLs from the last capture — enables "Original photo" projection. */
  captureImages?: string[];
  /** Initial artifact to project. Defaults to structured boxes-and-arrows. */
  initialArtifact?: Artifact;
  /** Persisted when the user switches inside the projection view. */
  onArtifactChange?: (artifact: Artifact) => void;
  /** Persist direct edits and apply them to the live items. Enables Edit mode. */
  onSaveEdits?: (edits: BoardEdit[]) => Promise<void>;
}

export type WhiteboardProjectionArtifact = 'structured' | 'original';

export function WhiteboardProjectionView({
  baseline,
  title,
  onClose,
  captureImages,
  initialArtifact = 'structured',
  onArtifactChange,
  onSaveEdits,
}: Props) {
  const [mode, setMode] = useState<Mode>('projector');
  const [artifact, setArtifact] = useState<Artifact>(initialArtifact);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Local edit state, layered over the baseline so dragging/deleting is live.
  const [positions, setPositions] = useState<Record<string, Pos>>({});
  const [deleted, setDeleted] = useState<Set<string>>(new Set());
  const [dragId, setDragId] = useState<string | null>(null);

  const surfaceRef = useRef<HTMLDivElement>(null);

  const posOf = useCallback(
    (id: string, fallback: Pos): Pos => positions[id] ?? fallback,
    [positions],
  );

  const dirty = Object.keys(positions).length > 0 || deleted.size > 0;
  const hasOriginal = (captureImages?.length ?? 0) > 0;
  const showingOriginal = artifact === 'original' && hasOriginal;

  const reset = useCallback(() => {
    setPositions({});
    setDeleted(new Set());
  }, []);

  const setArtifactMode = useCallback(
    (next: Artifact) => {
      if (next === 'original' && !hasOriginal) return;
      if (next === 'original' && editing) {
        reset();
        setEditing(false);
      }
      setArtifact(next);
      onArtifactChange?.(next);
    },
    [hasOriginal, editing, reset, onArtifactChange],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (editing && dirty) return; // don't lose edits to a stray Esc
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editing, dirty]);

  // Pointer drag: translate client coords to a 0–1 board fraction.
  const fractionFromEvent = useCallback((clientX: number, clientY: number): Pos | null => {
    const el = surfaceRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return null;
    // Invert the registration frame so a dragged box's stored position is in
    // board coordinates, consistent with how boxes are laid out.
    return {
      x: surfaceToBoard((clientX - r.left) / r.width),
      y: surfaceToBoard((clientY - r.top) / r.height),
    };
  }, []);

  useEffect(() => {
    if (!dragId) return;
    const onMove = (e: PointerEvent) => {
      const f = fractionFromEvent(e.clientX, e.clientY);
      if (f) setPositions((prev) => ({ ...prev, [dragId]: f }));
    };
    const onUp = () => setDragId(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragId, fractionFromEvent]);

  const handleSave = useCallback(async () => {
    if (!onSaveEdits || !dirty) return;
    const edits: BoardEdit[] = [];
    for (const id of deleted) edits.push({ type: 'delete', id });
    for (const [id, position] of Object.entries(positions)) {
      if (deleted.has(id)) continue;
      edits.push({ type: 'move', id, position });
    }
    setSaving(true);
    try {
      await onSaveEdits(edits);
      reset();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }, [onSaveEdits, dirty, deleted, positions, reset]);

  const projector = mode === 'projector';
  const surface = projector ? 'bg-black' : 'bg-[var(--bg-raised)]';
  const ink = projector ? '#e8f0ff' : '#1f2937';
  const accent = projector ? '#7dd3fc' : '#2563eb';
  const codeBg = projector ? 'rgba(125,211,252,0.18)' : 'rgba(37,99,235,0.10)';

  const visibleItems = baseline.items.filter((it) => !deleted.has(it.id));

  // Edges from CURRENT positions so they track drags; drop any touching a
  // deleted item or a missing endpoint.
  const posMap = new Map(visibleItems.map((it) => [it.id, posOf(it.id, it.position)] as const));
  const edges = baseline.relationships
    .map((r) => {
      const s = posMap.get(r.sourceItemId);
      const t = posMap.get(r.targetItemId);
      if (!s || !t) return null;
      return {
        x1: boardToSurfacePct(s.x), y1: boardToSurfacePct(s.y),
        x2: boardToSurfacePct(t.x), y2: boardToSurfacePct(t.y),
        directed: r.directionality === 'directed',
      };
    })
    .filter((e): e is NonNullable<typeof e> => e !== null);

  return (
    <div ref={surfaceRef} className={`fixed inset-0 z-[60] ${surface} overflow-hidden ${editing ? 'touch-none select-none' : ''}`}>
      {/* Control bar */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur text-white text-xs">
        <MonitorPlay className="h-3.5 w-3.5" />
        <span className="font-medium max-w-[180px] truncate">{title}</span>
        <span className="opacity-50">·</span>
        <button
          onClick={() => setMode(projector ? 'screen' : 'projector')}
          className="inline-flex items-center gap-1 hover:text-[var(--text-ter)]"
          title="Toggle projector / screen mode"
        >
          {projector ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
          {projector ? 'Projector' : 'Screen'}
        </button>

        <span className="opacity-50">·</span>
        <span className="inline-flex items-center rounded-full bg-[var(--bg-raised)]/10 p-0.5">
          <button
            type="button"
            onClick={() => setArtifactMode('structured')}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
              !showingOriginal ? 'bg-[var(--bg-raised)]/20 text-white' : 'hover:text-[var(--text-ter)]'
            }`}
            title="Project the system's neat boxes and arrows"
          >
            <LayoutGrid className="h-3.5 w-3.5" /> Structured
          </button>
          <button
            type="button"
            onClick={() => setArtifactMode('original')}
            disabled={!hasOriginal}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors ${
              showingOriginal ? 'bg-[var(--bg-raised)]/20 text-white' : hasOriginal ? 'hover:text-[var(--text-ter)]' : 'opacity-40 cursor-not-allowed'
            }`}
            title={hasOriginal ? 'Project the original captured photo' : 'No capture photo saved for this board'}
          >
            <Image className="h-3.5 w-3.5" /> Original
          </button>
        </span>

        {onSaveEdits && !showingOriginal && (
          <>
            <span className="opacity-50">·</span>
            <button
              onClick={() => { if (editing) reset(); setEditing((v) => !v); }}
              className={`inline-flex items-center gap-1 ${editing ? 'text-[var(--mustard)]' : 'hover:text-[var(--mustard)]'}`}
              title="Edit the board directly (drag to move, wipe to delete)"
            >
              <Pencil className="h-3.5 w-3.5" /> {editing ? 'Editing' : 'Edit'}
            </button>
          </>
        )}

        {editing && dirty && (
          <>
            <button onClick={reset} className="inline-flex items-center gap-1 hover:text-white/90" title="Discard edits">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-60"
              title="Save changes back to this board"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </button>
          </>
        )}

        <button onClick={onClose} className="inline-flex items-center gap-1 hover:text-[var(--coral)]" title="Exit (Esc)">
          <X className="h-3.5 w-3.5" /> Exit
        </button>
      </div>

      {editing && !showingOriginal && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-10 text-[11px] text-white/70 bg-black/30 px-2 py-0.5 rounded-full">
          Drag a box to move it · tap the trash to wipe it away
        </div>
      )}

      {showingOriginal ? (
        <div className={`absolute inset-0 flex items-center justify-center ${projector ? 'bg-black' : 'bg-[var(--hover-tint)]'}`}>
          {captureImages!.length === 1 ? (
            <img
              src={captureImages![0]}
              alt={`Original capture of ${title}`}
              className="max-w-full max-h-full object-contain"
              draggable={false}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center gap-1 p-4 overflow-x-auto">
              {captureImages!.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Capture tile ${i + 1} of ${captureImages!.length}`}
                  className="h-full w-auto max-w-none object-contain flex-shrink-0"
                  draggable={false}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
      {/* Corner registration marks — each L-vertex sits exactly at its board
          corner (0,0),(1,0),(1,1),(0,1) within the registration frame, so a
          re-capture maps the marks onto the same frame the boxes are drawn in. */}
      {([
        { bx: 0, by: 0, tx: '0',    ty: '0',    d: 'M14 0 L0 0 L0 14' },     // top-left
        { bx: 1, by: 0, tx: '-100%', ty: '0',   d: 'M14 0 L28 0 L28 14' },   // top-right
        { bx: 1, by: 1, tx: '-100%', ty: '-100%', d: 'M28 14 L28 28 L14 28' }, // bottom-right
        { bx: 0, by: 1, tx: '0',    ty: '-100%', d: 'M0 14 L0 28 L14 28' },  // bottom-left
      ] as const).map((m, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${boardToSurfacePct(m.bx)}%`,
            top: `${boardToSurfacePct(m.by)}%`,
            transform: `translate(${m.tx}, ${m.ty})`,
            color: ink,
          }}
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="2">
            <path d={m.d} />
          </svg>
        </div>
      ))}

      {/* Relationship edges */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <marker id="wb-proj-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
            <path d="M0,0 L6,3 L0,6 Z" fill={accent} />
          </marker>
        </defs>
        {edges.map((e, i) => (
          <line
            key={i}
            x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
            stroke={accent}
            strokeWidth={projector ? 0.5 : 0.35}
            vectorEffect="non-scaling-stroke"
            markerEnd={e.directed ? 'url(#wb-proj-arrow)' : undefined}
          />
        ))}
      </svg>

      {/* Boxes */}
      {visibleItems.map((it) => {
        const p = posOf(it.id, it.position);
        const isDragging = dragId === it.id;
        return (
          <div
            key={it.id}
            onPointerDown={(e) => {
              if (!editing) return;
              e.preventDefault();
              setDragId(it.id);
            }}
            className={`absolute -translate-x-1/2 -translate-y-1/2  px-3 py-2 max-w-[18%] text-center ${
              editing ? 'cursor-grab' : ''
            } ${isDragging ? 'cursor-grabbing ring-2 ring-amber-400 z-20' : ''}`}
            style={{
              left: `${boardToSurfacePct(p.x)}%`,
              top: `${boardToSurfacePct(p.y)}%`,
              border: `2px solid ${ink}`,
              color: ink,
              background: projector ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.9)',
            }}
          >
            {it.code && (
              <div
                className="absolute -top-2 -left-2 px-1.5 py-0.5 rounded font-mono text-[11px] font-bold"
                style={{ color: ink, background: codeBg, border: `1px solid ${ink}` }}
              >
                {it.code}
              </div>
            )}
            {editing && (
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => setDeleted((prev) => new Set(prev).add(it.id))}
                className="absolute -top-2 -right-2 p-1 rounded-full bg-[var(--coral)] text-white hover:bg-[var(--coral)] shadow"
                title="Wipe this box away"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            )}
            <div className="text-sm font-semibold leading-tight break-words">{it.title}</div>
          </div>
        );
      })}

      {visibleItems.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ color: ink }}>
          <p className="text-sm opacity-70">This board has no items to project.</p>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default WhiteboardProjectionView;
